import axios from 'axios';

import * as config from '@/../config/index.js';
import { decodeExp } from '@/auth/decodeExp';
import { getAuthSession } from '@/auth/session';
import { readAccessToken, readRefreshToken as readRefreshTokenSession, readSessionRole } from '@/auth/tabStorage';
import { showConnectionErrorToast } from '@/plugins/errorToasts';
import * as auth from '@/utils/auth.js';
import { isNetworkError } from '@/utils/isNetworkError';
import { saveReloginContext } from '@/utils/reloginContext';

// These auth endpoints are public, but they still legitimately return 401s
// for business cases like invalid credentials or expired reset tokens.
// Legacy refresh/logout handling must not intercept those responses.
const publicAuthPaths = new Set([
  '/auth/login',
  '/auth/register',
  '/auth/password/reset',
  '/auth/password/change-by-token',
]);

const isPublicAuthRequest = (requestUrl) => typeof requestUrl === 'string' && publicAuthPaths.has(requestUrl);

// External Go services (ae-stat, adserver) respond 403 for an invalid/missing bearer; treat both as refresh-and-retry.
const isAuthFailureStatus = (status) => status === 401 || status === 403;

// Refresh before sending if the token would expire within this window, so long-running requests don't hit mid-flight 401.
const JIT_REFRESH_BUDGET_MS = 5_000;
const isTokenExpiringSoon = (token) => {
  const exp = decodeExp(token);
  return exp != null && exp * 1000 - Date.now() < JIT_REFRESH_BUDGET_MS;
};

export default class Api {
  /**
   * @param {Object} options={}
   * @param {string} options.baseUrl - Base URL for the API service
   * @param {boolean} options.authRequired - Whether this service requires authentication headers
   * @param {boolean} options.lang - Whether to include language header
   * @param {boolean} options.role - Whether to include active role header
   * @param {Object} options.apiRef - Circular reference to the parent API object for shared state (token refresh, logout)
   */
  constructor(options = {}) {
    this.client =
      options.client ||
      axios.create({
        baseURL: options.baseUrl,
        timeout: 300000,
      });

    if (options.lang) {
      this.client.defaults.headers.common['X-LANG'] = process.env.LANGUAGE;
    }

    // Store reference to parent API object for interceptor callbacks (token refresh, logout)
    this.apiRef = options.apiRef;
    this.options = {
      ...options,
    };
    this.disguiseRequest = {
      token: null,
      roleId: null,
      isDisguiseRequest: false,
    };

    this.client.interceptors.request.use(
      async (config) => {
        window.localStorage.removeItem('login-form');

        // For `/stats/grouped`, `/stats/ranges` and `stats/fraud-tags`, the backend requires the `q` parameter as a JSON string.
        // Newer Axios versions serialize nested objects in `params` into query strings by default.
        // Example:
        //   Input: { q: { key: 'value', nested: { subKey: 'subValue' } } }
        //   Default latest Axios Result: q[key]=value&q[nested][subKey]=subValue
        //
        // The backend expects JSON-encoded `q`:
        //   Required Result: q={"key":"value","nested":{"subKey":"subValue"}}
        //
        // Fix: Use `JSON.stringify` to ensure the `q` parameter is sent as a JSON string.
        // Avoid double JSON.stringify by stringifying only if `q` is an object
        if (
          ['/stats/fraud-tags', '/stats/grouped', '/stats/ranges'].includes(config.url) &&
          typeof config.params?.q == 'object'
        ) {
          config.params.q = JSON.stringify(config.params.q);
        }

        // Do not send the current session token to public auth endpoints.
        // They should be evaluated against the credentials/token in the request body.
        if (this.options.authRequired && isPublicAuthRequest(config.url)) {
          return config;
        }

        let currentToken = readAccessToken();
        if (!currentToken) {
          return config;
        }
        if (!this.options.authRequired) {
          return config;
        }

        // JIT refresh; on failure, fall through with the stale token so the response interceptor handles it.
        if (
          !this.disguiseRequest.isDisguiseRequest &&
          !config.retry &&
          !config.skipAuthRefresh &&
          isTokenExpiringSoon(currentToken)
        ) {
          try {
            const tokens = await getAuthSession().refresh();
            currentToken = tokens.accessToken;
          } catch {
            config.skipAuthRefresh = true;
          }
        }

        const newConfig = { ...config };
        if (newConfig.headers?.Authorization) {
          return newConfig;
        }

        if (this.disguiseRequest.isDisguiseRequest) {
          newConfig.headers.Authorization = `Bearer ${this.disguiseRequest.token}`;
          newConfig.headers['X-ACTIVE-ROLE-ID'] = this.disguiseRequest.roleId;
          this.disguiseRequest.isDisguiseRequest = false;
        } else {
          newConfig.headers.Authorization = `Bearer ${currentToken}`;
        }
        return newConfig;
      },
      (e) => Promise.reject(e),
    );

    this.client.interceptors.response.use(
      (r) => r,
      async (error) => {
        if (error instanceof axios.Cancel) {
          throw error;
        }

        if (isNetworkError(error)) {
          showConnectionErrorToast();
        }

        const url = error?.config?.url;
        const publicAuthRequest = isPublicAuthRequest(url);
        const skipAuthRefresh = error?.config?.skipAuthRefresh;

        if (
          error.response &&
          (typeof url === 'string' ? url.includes('auth/refresh-token') : url?.pathname?.includes('auth/refresh-token'))
        ) {
          window.localStorage.setItem('login-form', 'true');
          this.apiRef.logout();
          return;
        }

        // Public auth 401/403s should go back to the form-level error handling.
        // Otherwise the legacy client treats them as an expired session and starts refresh/logout.
        if (publicAuthRequest && isAuthFailureStatus(error.response?.status)) {
          throw error;
        }

        if (skipAuthRefresh) {
          throw error;
        }

        const currentRefreshToken = readRefreshTokenSession();

        if (!currentRefreshToken && isAuthFailureStatus(error.response?.status)) {
          this.apiRef.logout();
          return;
        }

        if (
          !currentRefreshToken ||
          error.response === undefined ||
          !isAuthFailureStatus(error.response.status) ||
          error.config.retry
        ) {
          throw error;
        }

        if (!this.apiRef.refreshRequest) {
          this.apiRef.refreshRequest = getAuthSession()
            .refresh()
            .then((tokens) => {
              const queue = this.apiRef.queue;
              this.apiRef.queue = [];
              this.apiRef.refreshRequest = null;
              queue.forEach(({ retry }) => retry(tokens.accessToken));
            })
            .catch(() => {
              const queue = this.apiRef.queue;
              this.apiRef.queue = [];
              this.apiRef.refreshRequest = null;
              queue.forEach(({ fail }) => fail());
              if (getAuthSession().getState() === 'dead') {
                saveReloginContext({
                  url: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                  role: auth.readActiveRole(),
                });
                window.location = '/login';
              }
              // If state !== 'dead' (transient network error), do not redirect —
              // the next 401 will trigger another refresh attempt.
            });
        }

        const newRequest = {
          ...error.config,
          retry: true,
        };

        if (this.apiRef.refreshRequest) {
          return new Promise((resolve, reject) => {
            this.apiRef.queue.push({
              retry: (accessToken) => {
                newRequest.headers.Authorization = 'Bearer ' + accessToken;
                resolve(this.client(newRequest));
              },
              fail: () => reject(error),
            });
          });
        }

        return this.client(newRequest);
      },
    );
  }

  async login({ login, password, cfTurnstileToken }) {
    let data;
    try {
      const res = await this.client.post('/auth/login', {
        login,
        password,
        ...(cfTurnstileToken ? { cfTurnstileToken } : {}),
      });
      data = res.data;
    } catch (error) {
      // Attach challengeMode / retryAfterSec from augmented 401 responses so
      // Login.vue can escalate the widget appearance without re-parsing the error.
      if (error?.response?.status === 401) {
        const body = error.response.data ?? {};
        error.challengeMode = body.challengeMode ?? 'invisible';
        error.retryAfterSec = body.retryAfterSec ?? 0;
      }
      throw error;
    }

    return { token: data.accessToken, refreshToken: data.refreshToken };
  }

  logout() {
    window.localStorage.removeItem('entryUrl');
    window.localStorage.removeItem('entryRole');
  }

  prepareRequest(userData = null) {
    if (this.options.role) {
      this.client.defaults.headers.common['X-ACTIVE-ROLE-ID'] = config.default.rolesIds[readSessionRole()];
    }

    if (userData !== null) {
      this.disguiseRequest = userData;
      this.disguiseRequest.isDisguiseRequest = true;
    }
  }

  async request(method, url, params = {}, userData = null) {
    this.prepareRequest(userData);

    return this.client[method](url, params)
      .then(({ data }) => data)
      .catch((e) => Promise.reject(e));
  }

  async get(url, params = {}) {
    return this.request('get', url, params);
  }

  async post(url, params = {}, requestConfig = {}) {
    this.prepareRequest();

    return this.client
      .post(url, params, requestConfig)
      .then(({ data }) => data)
      .catch((e) => Promise.reject(e));
  }

  async postByUser(url, params = {}, userData = {}, requestConfig = {}) {
    this.prepareRequest(userData);

    return this.client
      .post(url, params, requestConfig)
      .then(({ data }) => data)
      .catch((e) => Promise.reject(e));
  }

  async put(url, params = {}) {
    return this.request('put', url, params);
  }

  async delete(url, params = {}) {
    return this.request('delete', url, params);
  }
}
